import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SafeHtmlPipe } from '../../pipe/safe-html.pipe';
import { SidebarWidgetComponent } from './app-sidebar-widget.component';
import { combineLatest, Subscription } from 'rxjs';
import { PermissionService } from '../../services/permission.service';
import { MenuVisibilityService } from '../../services/menu-visibility.service';

import { MenuSection } from './menu-config';

const COLLAPSED_SECTIONS_KEY = 'crm.sidebar.collapsedSections';
const ADVANCED_MODE_KEY = 'crm.sidebar.advancedMode';

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    SafeHtmlPipe,
    SidebarWidgetComponent
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent implements OnInit, OnDestroy {

  currentRoleName = '';

  sections: MenuSection[] = [];

  // Estado de colapsado por sección, persistido en localStorage (RF: secciones colapsables).
  // Clave = section.prefix; valor true = colapsada.
  collapsedSections: Record<string, boolean> = {};

  // "Modo avanzado": OFF por defecto. Cuando está apagado, se ocultan del menú las
  // herramientas técnicas marcadas con `advanced` (colas, probadores, webhooks).
  // Persistido en localStorage con el mismo patrón que el colapso de secciones.
  advancedMode = false;

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription: Subscription = new Subscription();

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService,
    private menuVisibility: MenuVisibilityService
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    this.currentRoleName = this.menuVisibility.roleName;

    this.advancedMode = this.loadAdvancedMode();

    this.buildSections();

    // Reconstruir el menú cuando llega/cambia la matriz de permisos (login / recarga async),
    // para que los ítems sin permiso de vista desaparezcan sin parpadeo permanente.
    this.subscription.add(
      this.permissionService.permissions$.subscribe(() => {
        this.buildSections();
        this.cdr.detectChanges();
      })
    );

    this.collapsedSections = this.loadCollapsedSections();

    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            this.cdr.detectChanges();
          }
        }
      )
    );

    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges();
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    this.sections.forEach(section => {
      section.items.forEach((nav, i) => {
        if (nav.subItems) {
          nav.subItems.forEach(subItem => {
            if (currentUrl === subItem.path) {
              // Si el usuario navega a algo dentro de una sección colapsada,
              // expandimos la sección para mostrar el contexto activo.
              if (this.collapsedSections[section.prefix]) {
                this.collapsedSections = { ...this.collapsedSections, [section.prefix]: false };
                this.saveCollapsedSections();
              }
              const key = `${section.prefix}-${i}`;
              this.openSubmenu = key;

              setTimeout(() => {
                const el = document.getElementById(key);
                if (el) {
                  this.subMenuHeights[key] = el.scrollHeight;
                  this.cdr.detectChanges();
                }
              });
            }
          });
        }
      });
    });
  }

  toggleSection(prefix: string) {
    this.collapsedSections = {
      ...this.collapsedSections,
      [prefix]: !this.collapsedSections[prefix]
    };
    this.saveCollapsedSections();
  }

  isSectionCollapsed(prefix: string): boolean {
    return !!this.collapsedSections[prefix];
  }

  // ── Modo avanzado ─────────────────────────────────────────────────────────

  toggleAdvancedMode(): void {
    this.advancedMode = !this.advancedMode;
    this.saveAdvancedMode();
    // Reconstruimos el menú en vivo para mostrar/ocultar las herramientas técnicas.
    this.buildSections();
    this.cdr.detectChanges();
  }

  // Solo mostramos el control cuando el usuario realmente tiene una sección con
  // herramientas avanzadas disponibles (hoy: Automatización). Evita ruido para
  // roles sin acceso a workflows.
  get hasAdvancedTools(): boolean {
    return this.sections.some(s => s.prefix === 'automation');
  }

  private loadAdvancedMode(): boolean {
    try {
      return localStorage.getItem(ADVANCED_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private saveAdvancedMode(): void {
    try {
      localStorage.setItem(ADVANCED_MODE_KEY, String(this.advancedMode));
    } catch {
      // localStorage no disponible (modo privado, cuota llena) — silenciar
    }
  }

  private loadCollapsedSections(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private saveCollapsedSections(): void {
    try {
      localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(this.collapsedSections));
    } catch {
      // localStorage no disponible (modo privado, cuota llena) — silenciar
    }
  }

  onSubmenuClick() {
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }

  // Las reglas de visibilidad (rol → sección Configuración y rutas reservadas,
  // matriz de permisos y modo avanzado) viven en MenuVisibilityService para que
  // el buscador global aplique EXACTAMENTE las mismas. Ver INC_CRM_024 inc02/inc03.
  private buildSections(): void {
    this.sections = this.menuVisibility.sections(this.advancedMode);
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByPrefix(_index: number, section: MenuSection): string {
    return section.prefix;
  }
}